import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { canCopyImageToClipboard, copyImageToClipboard } from "../../lib/clipboard.js";
import { formatFullDateTime, type TimeZonePref } from "../../lib/datetime.js";
import { useRealtimeStore } from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";

// Просмотр медиа «как в оригинальном Telegram»: фото и видео открываются
// по центру экрана на тёмном фоне, стрелками ←/→ листаются соседние
// вложения, Esc/клик по фону закрывает. Один экземпляр на приложение,
// открывается глобальным событием — так его могут вызывать лента, вкладка
// «Что в чате» в карточке пользователя и миниатюры в композере, не таская
// колбэки через пропсы. Заменил два раздельных лайтбокса (фото / видео).

export interface MediaViewerItem {
  kind: "photo" | "video";
  url: string;
  thumbnailUrl?: string;
  mimeType?: string;
  caption?: string;
  senderLabel?: string;
  occurredAt?: string;
  // Есть чат и сообщение — в панели появляется «Показать в чате».
  chatId?: string;
  messageId?: string;
  // Локальный файл из композера (objectURL): «Открыть оригинал» не имеет
  // смысла, а копировать можно.
  local?: boolean;
}

interface OpenPayload {
  items: MediaViewerItem[];
  index: number;
}

const EVENT_NAME = "tg:media-viewer";

export const openMediaViewer = (items: MediaViewerItem[], index = 0): void => {
  if (items.length === 0) {
    return;
  }
  const safeIndex = Math.max(0, Math.min(items.length - 1, index));
  window.dispatchEvent(
    new CustomEvent<OpenPayload>(EVENT_NAME, { detail: { items, index: safeIndex } })
  );
};

/** Одно фото по URL — совместимость со старым openLightbox(url). */
export const openPhotoViewer = (url: string): void => {
  openMediaViewer([{ kind: "photo", url }], 0);
};

export const MediaViewer = () => {
  const [state, setState] = useState<OpenPayload | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "busy" | "done" | "failed">("idle");
  const stateRef = useRef<OpenPayload | null>(null);
  stateRef.current = state;
  const timeZone: TimeZonePref = useUiPrefs((prefs) => prefs.timeZone);

  const close = useCallback((): void => {
    setState(null);
    setZoomed(false);
  }, []);

  const step = useCallback((delta: number): void => {
    setState((current) => {
      if (!current || current.items.length < 2) {
        return current;
      }
      const next = (current.index + delta + current.items.length) % current.items.length;
      return { ...current, index: next };
    });
    setZoomed(false);
  }, []);

  useEffect(() => {
    const handleOpen = (event: Event): void => {
      setState((event as CustomEvent<OpenPayload>).detail);
      setZoomed(false);
      setCopyState("idle");
    };
    // Клавиши слушаем в capture-фазе и глушим: Esc не должен закрывать
    // чат под просмотрщиком, стрелки — двигать курсор в поле ввода.
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!stateRef.current) {
        return;
      }
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        close();
      } else if (event.key === "ArrowLeft") {
        event.stopPropagation();
        event.preventDefault();
        step(-1);
      } else if (event.key === "ArrowRight") {
        event.stopPropagation();
        event.preventDefault();
        step(1);
      }
    };
    window.addEventListener(EVENT_NAME, handleOpen);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener(EVENT_NAME, handleOpen);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [close, step]);

  useEffect(() => {
    if (copyState !== "done" && copyState !== "failed") {
      return;
    }
    const handle = window.setTimeout(() => setCopyState("idle"), 1600);
    return () => window.clearTimeout(handle);
  }, [copyState]);

  useEffect(() => {
    setCopyState("idle");
  }, [state?.index]);

  if (!state) {
    return null;
  }

  const item = state.items[state.index];
  if (!item) {
    return null;
  }
  const hasMany = state.items.length > 1;
  const canJump = Boolean(item.chatId && item.messageId);
  const metaParts = [
    item.senderLabel,
    item.occurredAt ? formatFullDateTime(item.occurredAt, timeZone) : null
  ].filter((part): part is string => Boolean(part));

  return createPortal(
    <div
      className="media-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={item.kind === "video" ? "Просмотр видео" : "Просмотр фото"}
      onClick={close}
    >
      <div className="media-viewer__bar" onClick={(event) => event.stopPropagation()}>
        <div className="media-viewer__info">
          {hasMany ? (
            <span className="media-viewer__counter">
              {state.index + 1} из {state.items.length}
            </span>
          ) : null}
          {metaParts.length > 0 ? (
            <span className="media-viewer__meta">{metaParts.join(" · ")}</span>
          ) : null}
        </div>
        <div className="media-viewer__actions">
          {item.kind === "photo" && canCopyImageToClipboard() ? (
            <button
              type="button"
              className="button button--ghost"
              disabled={copyState === "busy"}
              onClick={() => {
                setCopyState("busy");
                void copyImageToClipboard(item.url).then((ok) =>
                  setCopyState(ok ? "done" : "failed")
                );
              }}
              title="Скопировать фото в буфер обмена"
            >
              <i className="ic ic-copy" aria-hidden="true" />{" "}
              {copyState === "done"
                ? "Скопировано ✓"
                : copyState === "failed"
                  ? "Не удалось"
                  : copyState === "busy"
                    ? "Копируем…"
                    : "Копировать"}
            </button>
          ) : null}
          {!item.local ? (
            <a
              className="button button--ghost"
              href={item.url}
              target="_blank"
              rel="noreferrer"
              title="Открыть оригинал в новой вкладке"
            >
              <i className="ic ic-download" aria-hidden="true" /> Оригинал
            </a>
          ) : null}
          {canJump ? (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                useRealtimeStore
                  .getState()
                  .requestScrollToMessage(item.chatId!, item.messageId!);
                close();
              }}
              title="Перейти к этому сообщению в чате"
            >
              <i className="ic ic-message" aria-hidden="true" /> Показать в чате
            </button>
          ) : null}
          <button
            type="button"
            className="icon-button media-viewer__close"
            onClick={close}
            aria-label="Закрыть"
            title="Закрыть (Esc)"
          >
            <i className="ic ic-close" aria-hidden="true" />
          </button>
        </div>
      </div>

      {hasMany ? (
        <button
          type="button"
          className="media-viewer__nav media-viewer__nav--prev"
          onClick={(event) => {
            event.stopPropagation();
            step(-1);
          }}
          aria-label="Предыдущее"
          title="Предыдущее (←)"
        >
          <i className="ic ic-back" aria-hidden="true" />
        </button>
      ) : null}

      <div
        className={zoomed ? "media-viewer__stage media-viewer__stage--zoomed" : "media-viewer__stage"}
        onClick={(event) => {
          // Клик по пустому месту сцены — закрыть; по самому медиа — нет.
          if (event.target === event.currentTarget) {
            close();
          }
        }}
      >
        {item.kind === "video" ? (
          <video
            key={item.url}
            className="media-viewer__video"
            controls
            autoPlay
            playsInline
            preload="metadata"
            poster={item.thumbnailUrl}
            onClick={(event) => event.stopPropagation()}
          >
            <source src={item.url} type={item.mimeType ?? "video/mp4"} />
          </video>
        ) : (
          <img
            key={item.url}
            src={item.url}
            alt={item.caption ?? ""}
            className="media-viewer__img"
            draggable={false}
            title={zoomed ? "Уменьшить" : "Увеличить"}
            onClick={(event) => {
              event.stopPropagation();
              setZoomed((value) => !value);
            }}
          />
        )}
      </div>

      {hasMany ? (
        <button
          type="button"
          className="media-viewer__nav media-viewer__nav--next"
          onClick={(event) => {
            event.stopPropagation();
            step(1);
          }}
          aria-label="Следующее"
          title="Следующее (→)"
        >
          <i className="ic ic-back" aria-hidden="true" />
        </button>
      ) : null}

      {item.caption ? (
        <div className="media-viewer__caption" onClick={(event) => event.stopPropagation()}>
          {item.caption}
        </div>
      ) : null}
    </div>,
    document.body
  );
};
